// SETTINGS V2 — one Notifikácie category card (SETTINGS_V2_SP5 §5.4 / §5.5 / §8).
//
// Designer NotifCat visuals + legacy semantics. Left accent border in the
// category-identity hex; soft neutral header (name/desc + "Všetko" master toggle);
// desktop/tablet 4-column grid rows; phone stacked rows with labeled In-App/Email
// toggles and NO Push column. Push is a permanently-disabled placeholder with a
// Radix tooltip (provider hoisted at the layout root — never add another). Each
// card decides its own row shape via useBreakpoint(); the page stays breakpoint-
// agnostic. Inline styles + scoped CSS-var tokens + SToggle + Radix (no MUI).
import React from "react";
import * as RadixTooltip from "@radix-ui/react-tooltip";

import { useAppSelector } from "../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { useBreakpoint } from "../../../../shared/hooks/useBreakpoint";
import { NotificationPreference } from "../../../../shared/notifications/models/notificationModels";
import { SToggle } from "../atoms";
import { NotifCategoryDef, eventTypeLabels } from "./notificationsV2Data";

interface NotificationCategoryCardV2Props {
  def: NotifCategoryDef;
  items: NotificationPreference[];
  index: number;
  // `busy` (= actionLoading): documented no-op — guard lives upstream (§2), the card
  // never visually disables, so it is intentionally not destructured/read.
  busy: boolean;
  onTogglePref: (
    eventType: number,
    field: "isInAppEnabled" | "isEmailEnabled"
  ) => void;
  onToggleCategoryAll: (
    def: NotifCategoryDef,
    items: NotificationPreference[]
  ) => void;
}

// Radix tooltip trigger + portalled content. The portalled node carries the scope
// classes + palette theme so the CSS vars resolve outside .settings-v2-root (§5.5,
// the SP3/SP4 portal trap). Own opaque dark background via inline var(--ink).
const PushTooltip: React.FC<{
  theme: string;
  text: string;
  children: React.ReactNode;
}> = ({ theme, text, children }) => (
  <RadixTooltip.Root>
    <RadixTooltip.Trigger asChild>{children}</RadixTooltip.Trigger>
    <RadixTooltip.Portal>
      <RadixTooltip.Content
        className={`settings-v2-root p-${theme} mod-nastavenia`}
        sideOffset={6}
        style={{
          background: "var(--ink)",
          color: "var(--bg)",
          fontSize: 11.5,
          lineHeight: 1.4,
          padding: "6px 10px",
          borderRadius: 7,
          maxWidth: 240,
          zIndex: 1400,
        }}
      >
        {text}
      </RadixTooltip.Content>
    </RadixTooltip.Portal>
  </RadixTooltip.Root>
);

const colHeadBase: React.CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: 8.5,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: "var(--faint)",
};

const NotificationCategoryCardV2: React.FC<NotificationCategoryCardV2Props> = ({
  def,
  items,
  index,
  onTogglePref,
  onToggleCategoryAll,
}) => {
  const { translate: t } = useTranslation("settings");
  const { isPhone } = useBreakpoint();
  const theme = useAppSelector((s) => s.settingsV2Ui.theme);

  const anyInAppOn = items.some((i) => i.isInAppEnabled);
  const pushText = t("settings:notifications.pushFootnote");

  const rowLabel = (pref: NotificationPreference) =>
    t(
      eventTypeLabels[pref.eventType] ||
        `settings:notifications.eventTypes.type${pref.eventType}`
    );

  return (
    <div
      style={{
        background: "var(--card)",
        border: "1px solid var(--border)",
        borderLeft: `3px solid ${def.accent}`,
        borderRadius: 10,
        boxShadow: "var(--shadow)",
        overflow: "hidden",
        animation: `sv2-card-reveal 0.4s ease-out ${index * 0.06}s both`,
      }}
    >
      {/* Header strip — neutral bg, name (+ desc on desktop/tablet) + Všetko master */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: isPhone ? 9 : 10,
          padding: isPhone ? "10px 13px" : "11px 16px",
          background: "var(--bg-soft)",
          borderBottom: "1px solid var(--border-soft)",
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: isPhone ? 10.5 : 11,
              fontWeight: 700,
              letterSpacing: isPhone ? "0.07em" : "0.08em",
              textTransform: "uppercase",
              color: "var(--ink)",
            }}
          >
            {t(def.titleKey)}
          </div>
          {!isPhone && (
            <div style={{ fontSize: 11.5, color: "var(--muted)", marginTop: 1 }}>
              {t(def.descKey)}
            </div>
          )}
        </div>
        {!isPhone && (
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 9.5,
              letterSpacing: "0.07em",
              textTransform: "uppercase",
              color: "var(--muted)",
            }}
          >
            {t("settings:notifications.allToggle")}
          </span>
        )}
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            minHeight: isPhone ? 44 : undefined,
          }}
        >
          <SToggle
            on={anyInAppOn}
            color={def.accent}
            ariaLabel={`${t("settings:notifications.allToggle")} · ${t(def.titleKey)}`}
            onToggle={() => onToggleCategoryAll(def, items)}
          />
        </span>
      </div>

      {/* Column headers — desktop/tablet only */}
      {!isPhone && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 90px 90px 70px",
            gap: 10,
            padding: "7px 16px",
            borderBottom: "1px solid var(--border-soft)",
          }}
        >
          <span style={colHeadBase}>{t("settings:notifications.eventType")}</span>
          <span style={{ ...colHeadBase, textAlign: "center" }}>
            {t("settings:notifications.inApp")}
          </span>
          <span style={{ ...colHeadBase, textAlign: "center" }}>
            {t("settings:notifications.email")}
          </span>
          <PushTooltip theme={theme} text={pushText}>
            <span style={{ ...colHeadBase, textAlign: "center", cursor: "help" }}>
              {t("settings:notifications.push")}*
            </span>
          </PushTooltip>
        </div>
      )}

      {/* Event rows */}
      {items.map((pref, i) =>
        isPhone ? (
          // Phone: stacked row — label line + labeled In-App/Email pair, NO Push.
          <div
            key={pref.eventType}
            style={{
              padding: "9px 13px",
              borderTop: i ? "1px solid var(--border-soft)" : "none",
            }}
          >
            <div style={{ fontSize: 13, color: "var(--ink-2)" }}>{rowLabel(pref)}</div>
            <div style={{ display: "flex", gap: 18, marginTop: 7 }}>
              <span style={phoneToggleLabel}>
                {t("settings:notifications.inApp")}
                <SToggle
                  on={pref.isInAppEnabled}
                  color={def.accent}
                  ariaLabel={`${rowLabel(pref)} · ${t("settings:notifications.inApp")}`}
                  onToggle={() => onTogglePref(pref.eventType, "isInAppEnabled")}
                />
              </span>
              <span style={phoneToggleLabel}>
                {t("settings:notifications.email")}
                <SToggle
                  on={pref.isEmailEnabled}
                  color={def.accent}
                  ariaLabel={`${rowLabel(pref)} · ${t("settings:notifications.email")}`}
                  onToggle={() => onTogglePref(pref.eventType, "isEmailEnabled")}
                />
              </span>
            </div>
          </div>
        ) : (
          // Desktop/tablet: 4-column grid row.
          <div
            key={pref.eventType}
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 90px 90px 70px",
              gap: 10,
              padding: "8px 16px",
              alignItems: "center",
              borderTop: i ? "1px solid var(--border-soft)" : "none",
            }}
          >
            <span
              style={{
                fontSize: 13,
                color: "var(--ink-2)",
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {rowLabel(pref)}
            </span>
            <span style={{ display: "flex", justifyContent: "center" }}>
              <SToggle
                on={pref.isInAppEnabled}
                color={def.accent}
                ariaLabel={`${rowLabel(pref)} · ${t("settings:notifications.inApp")}`}
                onToggle={() => onTogglePref(pref.eventType, "isInAppEnabled")}
              />
            </span>
            <span style={{ display: "flex", justifyContent: "center" }}>
              <SToggle
                on={pref.isEmailEnabled}
                color={def.accent}
                ariaLabel={`${rowLabel(pref)} · ${t("settings:notifications.email")}`}
                onToggle={() => onTogglePref(pref.eventType, "isEmailEnabled")}
              />
            </span>
            <span style={{ display: "flex", justifyContent: "center" }}>
              <PushTooltip theme={theme} text={pushText}>
                <span style={{ display: "inline-flex" }}>
                  <SToggle
                    on={false}
                    disabled
                    ariaLabel={`${rowLabel(pref)} · ${t("settings:notifications.push")}`}
                    onToggle={() => undefined}
                  />
                </span>
              </PushTooltip>
            </span>
          </div>
        )
      )}
    </div>
  );
};

const phoneToggleLabel: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 7,
  minHeight: 44, // ≥44px touch row (§8) — pads the pressable area, not the visual
  fontFamily: "var(--font-mono)",
  fontSize: 9.5,
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  color: "var(--muted)",
};

export default NotificationCategoryCardV2;
